/**
 * KnowledgeArchitecturePanel - Main orchestrator for Knowledge Architecture tab
 *
 * Fetches structural analysis, detects tier, computes FAIR scores,
 * and renders the containment diagram, detail panel, and comparison table.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useApiClient } from '../../../hooks/useApiClient';
import type {
  KnowledgeTier,
  OntologyStatistics,
  StructuralAnalysisData,
} from './types';
import { detectTier } from './tierDetector';
import { computeFairScores } from './fairDimensionScorer';
import { ContainmentDiagram } from './ContainmentDiagram';
import { TierDetailPanel } from './TierDetailPanel';
import { ComparisonTable } from './ComparisonTable';
import './KnowledgeArchitecturePanel.css';

// =============================================================================
// TYPES
// =============================================================================

interface KnowledgeArchitecturePanelProps {
  ontologyId: string;
  statistics: OntologyStatistics;
}

interface StructuralAnalysisResponse {
  ontologyId: string;
  ontologyName: string;
  namespace: string;
  version: string;
  analysis: StructuralAnalysisData;
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const KnowledgeArchitecturePanel: React.FC<KnowledgeArchitecturePanelProps> = ({
  ontologyId,
  statistics,
}) => {
  const { authFetch } = useApiClient();

  // Structural analysis state
  const [structural, setStructural] = useState<StructuralAnalysisData | null>(null);
  const [structuralLoading, setStructuralLoading] = useState(false);
  const [structuralError, setStructuralError] = useState<string | null>(null);

  // Selected tier for detail panel
  const [selectedTier, setSelectedTier] = useState<KnowledgeTier | null>(null);

  // Fetch structural analysis on mount
  useEffect(() => {
    if (!ontologyId) return;

    const fetchStructural = async () => {
      setStructuralLoading(true);
      setStructuralError(null);

      try {
        const response = await authFetch(
          `/api/ontology-improvement/${ontologyId}/structural`
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to fetch structural analysis');
        }

        const data: StructuralAnalysisResponse = await response.json();
        setStructural(data.analysis);
      } catch (err: unknown) {
        console.error('[KnowledgeArchitecturePanel] Structural analysis error:', err);
        setStructuralError(
          err instanceof Error ? err.message : 'Failed to load structural analysis'
        );
      } finally {
        setStructuralLoading(false);
      }
    };

    fetchStructural();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- authFetch is stable
  }, [ontologyId]);

  // Detect tier
  const detection = useMemo(
    () => detectTier(statistics, structural),
    [statistics, structural]
  );

  // Compute FAIR scores
  const fairScores = useMemo(
    () => computeFairScores(structural),
    [structural]
  );

  // Set initial selected tier to detected tier
  useEffect(() => {
    if (detection.tier && selectedTier === null) {
      setSelectedTier(detection.tier);
    }
  }, [detection.tier, selectedTier]);

  const activeTier = selectedTier ?? detection.tier;

  return (
    <div className="ka-panel">
      <div className="ka-panel-header">
        <h2 className="ka-panel-title">Knowledge Architecture</h2>
      </div>

      {structuralError && !structural && (
        <div className="ka-panel-error">
          <p>Structural analysis unavailable: {structuralError}</p>
          <p style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.5rem' }}>
            Educational content is still shown below. Metrics will display as &quot;unavailable&quot;.
          </p>
        </div>
      )}

      {/* Main layout: Diagram + Detail Panel */}
      <div className="ka-main-row">
        <ContainmentDiagram
          detectedTier={detection.tier}
          selectedTier={activeTier}
          onSelectTier={setSelectedTier}
          confidence={detection.confidence}
        />

        <TierDetailPanel
          selectedTier={activeTier}
          detection={detection}
          fairScores={fairScores}
          statistics={statistics}
          structural={structural}
          structuralLoading={structuralLoading}
          onSelectTier={setSelectedTier}
        />
      </div>

      {/* Comparison Table */}
      <ComparisonTable detectedTier={detection.tier} />

      {/* Detection signals */}
      <div className="ka-comparison-table-wrapper">
        <h4 className="ka-section-title">Detection Signals</h4>
        <div className="ka-metrics-grid">
          {detection.signals.map(signal => (
            <div
              key={signal.label}
              className={`ka-metric-card ka-metric-${signal.met ? 'achieved' : 'gap'}`}
            >
              <span className="ka-metric-label">{signal.label}</span>
              <span className="ka-metric-value" style={{ fontSize: '0.85rem' }}>
                {signal.value ?? '--'}
              </span>
              <span className={`ka-metric-status ka-status-${signal.met ? 'achieved' : 'gap'}`}>
                {signal.met ? 'Met' : 'Not met'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default KnowledgeArchitecturePanel;
